import type { PropType } from 'vue'
import { computed } from 'vue'

import BaseIcon from '@/components/BaseIcon.vue'
import { variants } from '@/utils/variants'

import type { VariantPropsOf } from '@/utils/variants'

import './style.css'

const styles = variants({
  base: 'icon-button',
  compoundVariants: [
    {
      className: 'icon-button__compound--primary-default',
      variants: {
        type: 'default',
        variant: 'primary',
      },
    },
    {
      className: 'icon-button__compound--primary-warning',
      variants: {
        type: 'warning',
        variant: 'primary',
      },
    },
    {
      className: 'icon-button__compound--primary-error',
      variants: {
        type: 'error',
        variant: 'primary',
      },
    },
    {
      className: 'icon-button__compound--primary-info',
      variants: {
        type: 'info',
        variant: 'primary',
      },
    },
    {
      className: 'icon-button__compound--primary-close',
      variants: {
        type: 'close',
        variant: 'primary',
      },
    },
    {
      className: 'icon-button__compound--primary-destructive',
      variants: {
        type: 'destructive',
        variant: 'primary',
      },
    },
    {
      className: 'icon-button__compound--secondary-default',
      variants: {
        type: 'default',
        variant: 'secondary',
      },
    },
    {
      className: 'icon-button__compound--secondary-warning',
      variants: {
        type: 'warning',
        variant: 'secondary',
      },
    },
    {
      className: 'icon-button__compound--secondary-error',
      variants: {
        type: 'error',
        variant: 'secondary',
      },
    },
    {
      className: 'icon-button__compound--secondary-info',
      variants: {
        type: 'info',
        variant: 'secondary',
      },
    },
    {
      className: 'icon-button__compound--secondary-destructive',
      variants: {
        type: 'destructive',
        variant: 'secondary',
      },
    },
    {
      className: 'icon-button__compound--outline-default',
      variants: {
        type: 'default',
        variant: 'outline',
      },
    },
    {
      className: 'icon-button__compound--outline-warning',
      variants: {
        type: 'warning',
        variant: 'outline',
      },
    },
    {
      className: 'icon-button__compound--outline-error',
      variants: {
        type: 'error',
        variant: 'outline',
      },
    },
    {
      className: 'icon-button__compound--outline-info',
      variants: {
        type: 'info',
        variant: 'outline',
      },
    },
    {
      className: 'icon-button__compound--outline-destructive',
      variants: {
        type: 'destructive',
        variant: 'outline',
      },
    },
    {
      className: 'icon-button__compound--outline-utility',
      variants: {
        type: 'utility',
        variant: 'outline',
      },
    },
  ],
  defaultVariants: {
    hoverType: 'default',
    rounded: true,
    size: 'default',
    type: 'default',
    variant: 'primary',
  },
  variants: {
    disabled: {
      true: 'icon-button--disabled',
    },
    hoverType: {
      black: 'icon-button__hover-type--black',
      close: 'icon-button__hover-type--close',
      default: '',
      destructive: 'icon-button__hover-type--destructive',
      disabled: 'icon-button__hover-type--disabled',
      error: '',
      info: '',
      warning: 'icon-button__hover-type--warning',
    },
    rounded: {
      true: 'icon-button--rounded',
    },
    size: {
      big: 'icon-button__size--big',
      default: 'icon-button__size--default',
      small: 'icon-button__size--small',
    },
    type: {
      close: 'icon-button__type--close',
      default: '',
      destructive: 'icon-button__type--destructive',
      disabled: 'icon-button__type--disabled',
      error: '',
      info: '',
      utility: 'icon-button__type--utility',
      warning: 'icon-button__type--warning',
    },
    variant: {
      outline: 'icon-button__variant--outline',
      primary: 'icon-button__variant--primary',
      secondary: 'icon-button__variant--secondary',
    },
  },
})

type Props = VariantPropsOf<typeof styles> & {
  iconSize?: IconSize
  iconType?: IconType
  iconVariant?: IconVariant
  name: IconName
}

const iconSizeMap: Record<NonNullable<Props['size']>, IconSize> = {
  big: '24',
  default: '20',
  small: '16',
}

export const IconButton = (props: Props) => {
  const classes = computed(() => {
    return styles({
      disabled: props.disabled,
      hoverType: props.hoverType,
      rounded: props.rounded,
      size: props.size,
      type: props.type,
      variant: props.variant,
    })
  })

  return (
    <button class={classes.value} disabled={props.disabled}>
      <BaseIcon
        name={props.name}
        size={props.iconSize ?? iconSizeMap[props.size ?? 'default']}
        type={props.iconType}
        variant={props.iconVariant}
      />
    </button>
  )
}

IconButton.props = {
  disabled: Boolean,
  hoverType: String as PropType<Props['hoverType']>,
  iconSize: String as PropType<IconSize>,
  iconType: String as PropType<IconType>,
  iconVariant: String as PropType<IconVariant>,
  name: {
    required: true,
    type: String as PropType<IconName>,
  },
  rounded: {
    default: true,
    type: Boolean,
  },
  size: String as PropType<Props['size']>,
  type: String as PropType<Props['type']>,
  variant: String as PropType<Props['variant']>,
}
